import { useClient } from '@cortex/react-query';
import type { ConsoleFeatureFlags } from '@cortex/types';
import { defaultConsoleFeatureFlags } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';

function useFeatureFlagsQuery() {
  const client = useClient();
  return useQuery({
    queryKey: ['org-settings', 'feature-flags'],
    queryFn: () => client.http.get('v1/org-settings/feature-flags').json<ConsoleFeatureFlags>(),
    staleTime: 60_000,
    // The API answers 503 while the flag service is down; keep asking
    // (backing off to 30 s) rather than settling on the all-off defaults.
    // A 4xx is an answer: stop.
    retry: (_failures, error) => {
      const status = (error as { response?: Response } | null)?.response?.status;
      return status === undefined || status >= 500;
    },
  });
}

/**
 * Org-level console feature flags (2026-07-07 nav review): Persistent
 * Agents, Refinement, Orchestrator, and Remote Realtime Tools stay hidden
 * from the nav — not deleted — until they're customer-ready. Routes stay
 * registered and reachable by URL.
 *
 * Values are resolved server-side per org (`GET /v1/org-settings/feature-flags`,
 * stored on OrgSetting). Setting/viewing flags is superuser-only, from the
 * Internal → Organizations pages (`PATCH /v1/admin/orgs/:orgId/feature-flags`).
 * Until the query resolves, the all-off defaults apply — flagged sections
 * never flash visible.
 */
export function useFeatureFlags(): ConsoleFeatureFlags {
  return useFeatureFlagsQuery().data ?? defaultConsoleFeatureFlags;
}

/**
 * The flags plus whether they are still unresolved (first load, or retrying
 * through a flag-service outage). A page gated on a flag waits on `pending`
 * instead of reading the all-off placeholder as "not enabled".
 */
export function useFeatureFlagsState(): { flags: ConsoleFeatureFlags; pending: boolean } {
  const query = useFeatureFlagsQuery();
  return { flags: query.data ?? defaultConsoleFeatureFlags, pending: query.isPending };
}
